import axios, { AxiosInstance, AxiosError } from 'axios';
import { AuthTokens } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.crc.it.com/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const tokens = this.getTokens();
        if (tokens?.access) {
          config.headers.Authorization = `Bearer ${tokens.access}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newTokens = await this.refreshToken();
            if (newTokens) {
              this.saveTokens(newTokens);
              originalRequest.headers.Authorization = `Bearer ${newTokens.access}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            this.clearAuth();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private getTokens(): AuthTokens | null {
    const tokensStr = localStorage.getItem('auth_tokens');
    if (!tokensStr || tokensStr === 'undefined') return null;
    try {
      return JSON.parse(tokensStr);
    } catch (e) {
      return null;
    }
  }

  private saveTokens(tokens: AuthTokens) {
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
  }

  private clearAuth() {
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('user');
  }

  private async refreshToken(): Promise<AuthTokens | null> {
    const tokens = this.getTokens();
    if (!tokens?.refresh) return null;

    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refresh: tokens.refresh,
    });

    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTH
  // ═══════════════════════════════════════════════════════════════════════════

  async login(email: string, password: string) {
    const response = await this.api.post('/auth/login', { email, password });
    const tokens = {
      access: response.data.access,
      refresh: response.data.refresh
    };
    this.saveTokens(tokens);

    const user = {
      ...response.data.user,
      userType: response.data.user.user_type,
      firstName: response.data.user.first_name,
      lastName: response.data.user.last_name,
      isActive: response.data.user.is_active,
      createdAt: response.data.user.created_at,
      updatedAt: response.data.user.updated_at
    };

    localStorage.setItem('user', JSON.stringify(user));

    // Dispatch custom event to notify App component of auth change
    window.dispatchEvent(new Event('auth-change'));

    return { ...response.data, user };
  }

  async logout() {
    try {
      const tokens = this.getTokens();
      if (tokens?.refresh) {
        await this.api.post('/auth/logout', { refresh_token: tokens.refresh });
      }
    } catch (error) {
      // Logout error
    } finally {
      this.clearAuth();
      // Dispatch custom event to notify App component of auth change
      window.dispatchEvent(new Event('auth-change'));
    }
  }

  async forgotPassword(email: string) {
    const response = await this.api.post('/auth/forgot-password', { email });
    return response.data;
  }

  async verifyResetOTP(email: string, otp: string) {
    const response = await this.api.post('/auth/verify-reset-otp', { email, otp });
    return response.data;
  }

  async resetPassword(email: string, otp: string, newPassword: string) {
    const response = await this.api.post('/auth/reset-password', { email, otp, newPassword });
    return response.data;
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (!userStr || userStr === 'undefined') return null;
    try{
      return JSON.parse(userStr);
    } catch (e) {
      return null;
    }
  }

  isAuthenticated(): boolean {
    const tokens = this.getTokens();
    return !!tokens?.access;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPLAINTS
  // ═══════════════════════════════════════════════════════════════════════════

  async createComplaint(data: {
    evaluator: string;
    company: string;
    description: string;
    locationName: string;
    latitude?: number;
    longitude?: number;
  }) {
    const response = await this.api.post('/complaints', data);
    return response.data;
  }

  async getComplaints(status?: string, page = 1, limit = 12) {
    const params: any = { page, limit };
    if (status) params.status = status;
    const response = await this.api.get('/complaints', { params });
    const results = (response.data.results || response.data).map((c: any) => ({
      ...c,
      id: c.id || c._id,
    }));
    return {
      results,
      total: response.data.total ?? results.length,
      page: response.data.page ?? page,
      limit: response.data.limit ?? limit,
      totalPages: response.data.totalPages ?? 1,
    };
  }

  async getComplaint(id: string) {
    const response = await this.api.get(`/complaints/${id}`);
    const c = response.data;
    return { ...c, id: c.id || c._id };
  }

  async updateComplaintStatus(id: string, status: string) {
    const response = await this.api.patch(`/complaints/${id}/status`, { status });
    return response.data;
  }

  async startWork(id: string) {
    const response = await this.api.post(`/complaints/${id}/start_work`);
    return response.data;
  }

  async submitResolution(id: string) {
    const response = await this.api.post(`/complaints/${id}/submit_resolution`);
    return response.data;
  }

  async acceptComplaint(id: string) {
    const response = await this.api.post(`/complaints/${id}/accept`);
    return response.data;
  }

  async closeComplaintByAdmin(id: string) {
    const response = await this.api.post(`/complaints/${id}/close-by-admin`);
    return response.data;
  }

  async requestRework(id: string, notes: string) {
    const response = await this.api.post(`/complaints/${id}/rework`, { notes });
    return response.data;
  }

  async extendComplaintSla(id: string, data: {
    additionalHours: number;
    resetToFullCycle?: boolean;
    justification: string;
  }) {
    const response = await this.api.patch(`/complaints/${id}/extend-sla`, data);
    return response.data;
  }

  async uploadAfterPhoto(
    complaintId: string,
    photo: File,
    latitude?: number,
    longitude?: number,
    caption?: string
  ) {
    const formData = new FormData();
    formData.append('complaint', complaintId);
    formData.append('file', photo);
    formData.append('photoType', 'after');
    if (latitude !== undefined) formData.append('latitude', latitude.toString());
    if (longitude !== undefined) formData.append('longitude', longitude.toString());
    if (caption) formData.append('description', caption);

    const response = await this.api.post(
      '/complaint-photos/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async deleteComplaint(id: string) {
    await this.api.delete(`/complaints/${id}`);
  }

  async getComplaintStatistics() {
    const response = await this.api.get('/complaints/statistics');
    return response.data;
  }

  async searchComplaints(query: string) {
    const response = await this.api.get('/complaints/search', {
      params: { q: query },
    });
    const complaints = response.data.results || response.data;
    return complaints.map((c: any) => ({
      ...c,
      id: c.id || c._id,
    }));
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DASHBOARD
  // ═══════════════════════════════════════════════════════════════════════════

  async getDashboardStats() {
    // Call role-specific dashboard endpoint based on user type
    const user = this.getCurrentUser();
    let endpoint = '/dashboard/admin';
    if (user?.userType === 'evaluator') endpoint = '/dashboard/evaluator';
    else if (user?.userType === 'company_manager') endpoint = '/dashboard/company-manager';

    const response = await this.api.get(endpoint);
    const data = response.data;

    // Transform backend response to frontend format
    return {
      totalComplaints: data.overview?.totalComplaints || 0,
      pendingComplaints: data.statusBreakdown?.pending || 0,
      inProgressComplaints: data.statusBreakdown?.in_progress || 0,
      resolvedComplaints: data.statusBreakdown?.resolved || 0,
      reworkRequestedComplaints: data.statusBreakdown?.rework_requested || 0,
      closedComplaints: data.statusBreakdown?.closed || 0,
      slaBreachCount: data.alerts?.overdueComplaints || 0,
      avgResolutionTime: 0,
      totalCompanies: data.overview?.totalCompanies || 0,
      activeCompanies: data.overview?.totalCompanies || 0,
      totalEvaluators: data.overview?.totalEvaluators || 0,
      totalManagers: 0,
      todayComplaints: data.overview?.todayComplaints || 0,
      weekComplaints: 0,
      monthComplaints: data.overview?.monthComplaints || 0,
      activeComplaints: data.overview?.activeComplaints || 0,
      slaCompliance: 0,
      overdueComplaints: data.alerts?.overdueComplaints || 0,
      expiringSoonComplaints: data.alerts?.slaWarningComplaints || 0,
      monthly_trends: data.monthly_trends
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPANIES
  // ═══════════════════════════════════════════════════════════════════════════

  async getCompanies() {
    const response = await this.api.get('/companies');
    const companies = response.data.results || response.data;

    return companies.map((company: any) => ({
      id: company.id || company._id,
      companyName: company.companyName,
      commercialRegistration: company.commercialRegistration,
      email: company.email,
      phone: company.phone,
      address: company.address,
      slaHours: company.customSlaHours || 24,
      isActive: company.isActive,
      createdAt: company.createdAt,
      updatedAt: company.updatedAt,
    }));
  }

  async createCompany(data: any) {
    const backendData = {
      companyName: data.companyName,
      commercialRegistration: data.commercialRegistration,
      email: data.email,
      phone: data.phone,
      address: data.address || '',
      customSlaHours: data.slaHours || null,
      ownerFirstName: data.ownerFirstName,
      ownerLastName: data.ownerLastName,
      ownerEmail: data.ownerEmail,
      ownerPassword: data.ownerPassword,
      ownerPhone: data.ownerPhone || '',
    };
    const response = await this.api.post('/companies', backendData);
    return response.data;
  }

  async updateCompany(id: number, data: any) {
    const backendData: any = {
      companyName: data.companyName,
      // commercialRegistration is immutable and cannot be updated
      email: data.email,
      phone: data.phone,
      address: data.address || '',
      customSlaHours: data.slaHours || null,
    };
    if (data.ownerPassword && data.ownerPassword.trim() !== '') {
      backendData.ownerPassword = data.ownerPassword;
    }
    const response = await this.api.patch(`/companies/${id}`, backendData);
    return response.data;
  }

  async deleteCompany(id: number) {
    const response = await this.api.delete(`/companies/${id}`);
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // USERS
  // ═══════════════════════════════════════════════════════════════════════════

  async getUsers(userType?: string) {
    const params = userType ? { userType } : {};
    const response = await this.api.get('/users', { params });
    const users = response.data.results || response.data;

    return users.map((user: any) => ({
      ...user,
      id: user._id || user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      userType: user.userType,
      isActive: user.isActive,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
      dateJoined: user.createdAt
    }));
  }

  async createUser(data: any) {
    const response = await this.api.post('/users', data);
    return response.data;
  }

  async updateUser(id: number, data: any) {
    const response = await this.api.patch(`/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: number) {
    const response = await this.api.delete(`/users/${id}`);
    return response.data;
  }

  async activateUser(id: number) {
    const response = await this.api.patch(`/users/${id}/activate`);
    return response.data;
  }

  async deactivateUser(id: number) {
    const response = await this.api.patch(`/users/${id}/deactivate`);
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // COMMENTS
  // ═══════════════════════════════════════════════════════════════════════════

  async getComplaintComments(complaintId: string) {
    const response = await this.api.get('/complaint-comments', {
      params: { complaint: complaintId },
    });
    return response.data.results || response.data;
  }

  async createComplaintComment(complaintId: string, comment: string) {
    const response = await this.api.post('/complaint-comments', {
      complaint: complaintId,
      commentText: comment,
    });
    return response.data;
  }

  async deleteComplaintComment(id: number) {
    const response = await this.api.delete(`/complaint-comments/${id}`);
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VIOLATION TYPES
  // ═══════════════════════════════════════════════════════════════════════════

  async getViolationTypes(companyId: string) {
    const response = await this.api.get(`/violation-types/by-company/${companyId}`);
    const data = response.data.results || response.data;
    return data.map((vt: any) => ({
      ...vt,
      id: vt._id || vt.id,
    }));
  }

  async createViolationType(data: { company: string; name: string; price: number }) {
    const response = await this.api.post('/violation-types', data);
    const vt = response.data;
    return { ...vt, id: vt._id || vt.id };
  }

  async updateViolationType(id: string, data: { name?: string; price?: number }) {
    const response = await this.api.patch(`/violation-types/${id}`, data);
    const vt = response.data;
    return { ...vt, id: vt._id || vt.id };
  }

  async deleteViolationType(id: string) {
    const response = await this.api.delete(`/violation-types/${id}`);
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DEDUCTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  async getCompanyDeductions(companyId?: string | number) {
    const response = await this.api.get('/company-deductions', {
      params: companyId ? { company: companyId } : {},
    });
    const data = response.data.results || response.data;
    return data.map((d: any) => ({ ...d, id: d._id || d.id }));
  }

  async createCompanyDeduction(data: any) {
    const response = await this.api.post('/company-deductions', data);
    return response.data;
  }

  async updateCompanyDeduction(id: string, data: any) {
    const response = await this.api.patch(`/company-deductions/${id}`, data);
    return response.data;
  }

  async deleteCompanyDeduction(id: string) {
    const response = await this.api.delete(`/company-deductions/${id}`);
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPANY MANAGERS
  // ═══════════════════════════════════════════════════════════════════════════

  async getCompanyManagers(companyId?: string) {
    const response = await this.api.get('/managers', {
      params: companyId ? { companyId } : {},
    });
    return response.data;
  }

  async createCompanyManager(data: any) {
    const response = await this.api.post('/managers', data);
    return response.data;
  }

  async updateCompanyManager(id: string, data: any) {
    const response = await this.api.patch(`/managers/${id}`, data);
    return response.data;
  }

  async deleteCompanyManager(id: string) {
    const response = await this.api.delete(`/managers/${id}`);
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SLA CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════════

  async getSLAConfigurations() {
    const response = await this.api.get('/sla-config');
    return response.data.results || response.data;
  }

  async getCurrentSLA() {
    const response = await this.api.get('/sla-config/current');
    return response.data;
  }

  async getSLAHistory() {
    const response = await this.api.get('/sla-config/history');
    return response.data;
  }

  async updateSLA(data: any) {
    const response = await this.api.post('/sla-config', data);
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EVALUATORS
  // ═══════════════════════════════════════════════════════════════════════════

  async getEvaluators() {
    const response = await this.api.get('/evaluators');
    return response.data.results || response.data;
  }

  async createEvaluator(data: any) {
    const response = await this.api.post('/evaluators', data);
    return response.data;
  }

  async updateEvaluator(id: number, data: any) {
    const response = await this.api.patch(`/evaluators/${id}`, data);
    return response.data;
  }

  async deleteEvaluator(id: number) {
    const response = await this.api.delete(`/evaluators/${id}`);
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // REPORTS
  // ═══════════════════════════════════════════════════════════════════════════

  async getEvaluatorDailyReports(params?: any) {
    const response = await this.api.get('/reports/evaluator-daily', { params });
    return response.data.results || response.data;
  }

  async getEvaluatorMonthlyReports(params?: any) {
    const response = await this.api.get('/reports/evaluator-monthly', { params });
    return response.data.results || response.data;
  }

  async getCompanyPerformanceReports(params?: any) {
    const response = await this.api.get('/reports/company-performance', { params });
    return response.data.results || response.data;
  }

  async getComplaintTimeLogs(complaintId?: string) {
    const params = complaintId ? { complaint: complaintId } : {};
    const response = await this.api.get('/reports/time-logs', { params });
    return response.data.results || response.data;
  }

  async downloadReportPDF(reportType: string, id: number) {
    const response = await this.api.get(`/pdf/${reportType}/${id}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getReports() {
    const response = await this.api.get('/reports');
    return response.data.results || response.data;
  }

  async generateReport(data: any) {
    const response = await this.api.post('/reports', data);
    return response.data;
  }

  async downloadReport(reportId: number) {
    const response = await this.api.get(`/reports/${reportId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Detailed Reports methods
  async getEvaluatorDailyReport(evaluatorId: string, startDate: string, endDate: string) {
    const response = await this.api.get(`/reports/evaluators/${evaluatorId}/daily`, {
      params: { start_date: startDate, end_date: endDate }
    });
    return response.data;
  }

  async getEvaluatorMonthlyReport(evaluatorId: string, year: number, month: number) {
    const response = await this.api.get(`/reports/evaluators/${evaluatorId}/monthly`, {
      params: { year, month }
    });
    return response.data;
  }

  async getAllEvaluatorsMonthly(year: number, month: number) {
    const response = await this.api.get('/reports/evaluators/all-monthly', {
      params: { year, month }
    });
    return response.data;
  }

  async getCompanyMonthlyReport(companyId: string, year: number, month: number) {
    const response = await this.api.get(`/reports/companies/${companyId}/monthly`, {
      params: { year, month }
    });
    return response.data;
  }

  async getAllCompaniesMonthly(year: number, month: number) {
    const response = await this.api.get('/reports/companies/all-monthly', {
      params: { year, month }
    });
    return response.data;
  }

  async getCompanyPerformanceComparison(year: number, month: number) {
    const response = await this.api.get('/reports/companies/performance-comparison', {
      params: { year, month }
    });
    return response.data;
  }

  // PDF Download methods - Match backend routes
  async downloadEvaluatorDailyReportPDF(evaluatorId: string, startDate: string, endDate: string) {
    const response = await this.api.get(`/reports/evaluators/${evaluatorId}/daily/pdf`, {
      params: { start_date: startDate, end_date: endDate },
      responseType: 'blob'
    });
    return response.data;
  }

  async downloadEvaluatorMonthlyReportPDF(evaluatorId: string, year: number, month: number) {
    const response = await this.api.get(`/reports/evaluators/${evaluatorId}/monthly/pdf`, {
      params: { year, month },
      responseType: 'blob'
    });
    return response.data;
  }

  async downloadCompanyMonthlyReportPDF(companyId: string, year: number, month: number) {
    const response = await this.api.get(`/reports/companies/${companyId}/monthly/pdf`, {
      params: { year, month },
      responseType: 'blob'
    });
    return response.data;
  }

  async downloadAllEvaluatorsMonthlyPDF(year: number, month: number) {
    const response = await this.api.get('/reports/evaluators/all-monthly/pdf', {
      params: { year, month },
      responseType: 'blob'
    });
    return response.data;
  }

  async downloadAllCompaniesMonthlyPDF(year: number, month: number) {
    const response = await this.api.get('/reports/companies/all-monthly/pdf', {
      params: { year, month },
      responseType: 'blob'
    });
    return response.data;
  }

  async downloadPerformanceComparisonPDF(year: number, month: number) {
    const response = await this.api.get('/reports/companies/performance-comparison/pdf', {
      params: { year, month },
      responseType: 'blob'
    });
    return response.data;
  }

  // Single deduction (خطية) PDF receipt
  async downloadSingleDeductionPDF(deductionId: string | number) {
    const response = await this.api.get(`/pdf/deductions/${deductionId}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Deductions (خطايا) full table PDF
  async downloadDeductionsPDF(companyId?: string) {
    const response = await this.api.get('/pdf/deductions', {
      responseType: 'blob',
      params: companyId ? { companyId } : {},
    });
    return response.data;
  }

  // Single complaint PDF
  async downloadComplaintPDF(complaintId: string) {
    const response = await this.api.get(`/pdf/complaint/${complaintId}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // NOTIFICATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  async getNotifications(params?: { isRead?: boolean; type?: string }) {
    const response = await this.api.get('/notifications', { params });
    return response.data;
  }

  async getUnreadCount() {
    const response = await this.api.get('/notifications/unread-count');
    return response.data.unreadCount;
  }

  async markNotificationAsRead(notificationId: string) {
    const response = await this.api.patch(`/notifications/${notificationId}/read`);
    return response.data;
  }

  async markAllNotificationsAsRead() {
    const response = await this.api.patch('/notifications/read-all');
    return response.data;
  }

  async deleteNotification(notificationId: string) {
    const response = await this.api.delete(`/notifications/${notificationId}`);
    return response.data;
  }

  async deleteAllReadNotifications() {
    const response = await this.api.delete('/notifications/read-all/clear');
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;
